import React, { useEffect } from "react";
import useWorkflowNavigation from "../../hooks/sendMoney/useWorkflowNavigation";

// types
import type { SendMoneyWorkflow } from "../../types/sendMoney";

// ui components
import FlowScreen from "../../components/Layouts/FlowScreen";
import SingleColumnLayout from "../../components/Layouts/SingleColumnLayout";
import DynamicFormRenderer from "../../components/DynamicForms/DynamicFormRenderer";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import SendMoneyFooter from "../../components/SendMoney/SendMoneyFooter";
import SendMoneyReviewScreen from "./SendMoneyReviewScreen";

interface SendMoneyWorkflowScreenProps {
  workflow: SendMoneyWorkflow;
}

const SendMoneyWorkflowScreen = ({ workflow }: SendMoneyWorkflowScreenProps): React.ReactElement => {
  const nav = useWorkflowNavigation({ workflow });
  const isReview = Boolean(nav.review);

  // Same mounted screen for every step, so the browser keeps the previous page's
  // scroll position: reset on each page change and on entering/leaving Review.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [nav.page.sequenceId, isReview]);

  if (nav.review) {
    return <SendMoneyReviewScreen review={nav.review} transactionContextId={workflow.transactionContextId} />;
  }

  return (
    <FlowScreen flowTitle={nav.page.title} showBackButton className="v3">
      <SingleColumnLayout>
        {nav.page.subTitle && <p className="text-secondary">{nav.page.subTitle}</p>}
        <DynamicFormRenderer
          key={nav.formKey}
          sections={nav.sections}
          initialValues={nav.initialValues}
          serverErrors={nav.serverErrors}
          submitLabel={nav.page.nextButton.title}
          submitting={nav.submitting}
          onChange={nav.updateValues}
          onSubmit={nav.next}
        />

        <SendMoneyFooter
          className="mt-24"
          content={nav.page.pageFooter?.content}
          disclaimers={nav.page.pageFooter?.disclaimers}
        />
      </SingleColumnLayout>

      <ApiErrorModal />
    </FlowScreen>
  );
};

export default SendMoneyWorkflowScreen;